/**
 * @file React hook — useMaterialAnalysisData.ts: form/state logic for contributor dashboard.
 */

import { useCallback, useEffect, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";
import { API_URLS } from "../../../../../constants/apiUrls";

/** Type definition — Sub Material. */
export interface SubMaterial {
  id: number;
  name: string;
}

/** Type definition — Material. */
export interface Material {
  id: number;
  name: string;
  code?: string;
  subMaterials: SubMaterial[];
}

/** Type definition — Manufacturer. */
export interface Manufacturer {
  id: number;
  name: string;
}

/** Type definition — Technology. */
export interface Technology {
  id: number;
  name: string;
}

const normalizeName = (value: any): string =>
  typeof value === "string"
    ? value
    : String(
        value?.name ??
          value?.label ??
          value?.title ??
          value?.manufacturerName ??
          value?.technologyName ??
          "Unnamed"
      );

/** Hook: use Material Analysis Data. */
export const useMaterialAnalysisData = (materialId: number | null) => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [subMaterials, setSubMaterials] = useState<SubMaterial[]>([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [technologies, setTechnologies] = useState<Technology[]>([]);
  const [loading, setLoading] = useState(true);
  const [subMaterialsLoading, setSubMaterialsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadMaterials = useCallback(async () => {
    try {
      const res = await axiosClient.get("/materials");
      const payload = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];
      const normalized: Material[] = payload
        .map((item: any) => {
          const id = Number(item?.id ?? item?.materialId);
          if (Number.isNaN(id)) return null;
          const children = Array.isArray(item?.subMaterials)
            ? item.subMaterials
                .map((sub: any) => {
                  const subId = Number(sub?.id ?? sub?.subMaterialId);
                  if (Number.isNaN(subId)) return null;
                  return {
                    id: subId,
                    name: normalizeName(sub),
                  };
                })
                .filter(Boolean) as SubMaterial[]
            : [];
          return {
            id,
            name: normalizeName(item),
            code: typeof item?.code === "string" ? item.code : undefined,
            subMaterials: children,
          };
        })
        .filter(Boolean) as Material[];
      setMaterials(normalized);
      setError(null);
    } catch (err) {
      console.error("Failed to load materials", err);
      setMaterials([]);
      setError("Failed to load materials");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadManufacturers = useCallback(async (materialId: number) => {
    try {
      const res = await axiosClient.get(
        API_URLS.MANUFACTURER_SPECIFICITIES.BY_MATERIAL(materialId)
      );
      const payload = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];
      const normalized: Manufacturer[] = payload
        .map((item: any) => {
          const id = Number(item?.id ?? item?.manufacturerId);
          if (Number.isNaN(id)) return null;
          return {
            id,
            name: normalizeName(item),
          };
        })
        .filter(Boolean) as Manufacturer[];
      setManufacturers(normalized);
    } catch (err) {
      console.error("Failed to load manufacturers", err);
      setManufacturers([]);
    }
  }, []);

  const loadSubMaterials = useCallback(async (selectedMaterialId: number) => {
    setSubMaterialsLoading(true);
    try {
      const res = await axiosClient.get(
        API_URLS.SUB_MATERIALS.BY_MATERIAL(selectedMaterialId)
      );
      const payload = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];
      const normalized: SubMaterial[] = payload
        .map((item: any) => {
          const id = Number(item?.id ?? item?.subMaterialId);
          if (Number.isNaN(id)) return null;
          return {
            id,
            name: normalizeName(item),
          };
        })
        .filter(Boolean) as SubMaterial[];
      setSubMaterials(normalized);
    } catch (err) {
      console.error("Failed to load sub-materials", err);
      setSubMaterials([]);
    } finally {
      setSubMaterialsLoading(false);
    }
  }, []);

  const loadTechnologies = useCallback(async (materialId: number) => {
    try {
      const res = await axiosClient.get(
        API_URLS.TECHNOLOGICAL_SPECIFICITIES.BY_MATERIAL(materialId)
      );
      const payload = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];
      const normalized: Technology[] = payload
        .map((item: any) => {
          const id = Number(item?.id ?? item?.technologyId);
          if (Number.isNaN(id)) return null;
          return {
            id,
            name: normalizeName(item),
          };
        })
        .filter(Boolean) as Technology[];
      setTechnologies(normalized);
    } catch (err) {
      console.error("Failed to load technologies", err);
      setTechnologies([]);
    }
  }, []);

  useEffect(() => {
    loadMaterials();
  }, [loadMaterials]);

  useEffect(() => {
    if (!materialId) {
      setSubMaterials([]);
      setManufacturers([]);
      setTechnologies([]);
      return;
    }

    loadSubMaterials(materialId);
    loadManufacturers(materialId);
    loadTechnologies(materialId);
  }, [materialId, loadSubMaterials, loadManufacturers, loadTechnologies]);

  return {
    materials,
    subMaterials,
    manufacturers,
    technologies,
    loading,
    subMaterialsLoading,
    error,
    refetch: loadMaterials,
  };
};

